"use client";

import {
  createContext,
  startTransition,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  type ReactNode
} from "react";

export type CartItem = {
  id: number;
  slug: string;
  sku: string | null;
  name: string;
  image: string;
  price: number;
  oldPrice: number;
  unit: string | null;
  qty: number;
};

type CartContextValue = {
  isReady: boolean;
  items: CartItem[];
  totalQty: number;
  totalAmount: number;
  addItem: (item: Omit<CartItem, "qty">, quantity?: number) => void;
  updateQty: (slug: string, quantity: number) => void;
  removeItem: (slug: string) => void;
  clearCart: () => void;
};

const STORAGE_KEY = "bitstone_cart_v1";
const CartContext = createContext<CartContextValue | null>(null);

function readStoredCart() {
  if (typeof window === "undefined") {
    return [];
  }

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return [];
    }

    const parsed = JSON.parse(raw) as CartItem[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeStoredCart(items: CartItem[]) {
  if (typeof window === "undefined") {
    return;
  }

  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
}

export function CartProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<CartItem[]>([]);
  const [isReady, setIsReady] = useState(false);

  useLayoutEffect(() => {
    startTransition(() => {
      setItems(readStoredCart());
      setIsReady(true);
    });
  }, []);

  useEffect(() => {
    if (!isReady) {
      return;
    }

    writeStoredCart(items);
  }, [isReady, items]);

  const value = useMemo<CartContextValue>(() => {
    const totalQty = items.reduce((sum, item) => sum + item.qty, 0);
    const totalAmount = items.reduce((sum, item) => sum + item.qty * item.price, 0);

    return {
      isReady,
      items,
      totalQty,
      totalAmount,
      addItem(item, quantity = 1) {
        setItems((currentItems) => {
          const nextQuantity = Math.max(1, quantity);
          const existing = currentItems.find((currentItem) => currentItem.slug === item.slug);

          if (existing) {
            return currentItems.map((currentItem) =>
              currentItem.slug === item.slug
                ? { ...currentItem, qty: currentItem.qty + nextQuantity }
                : currentItem
            );
          }

          return [...currentItems, { ...item, qty: nextQuantity }];
        });
      },
      updateQty(slug, quantity) {
        setItems((currentItems) =>
          currentItems
            .map((item) =>
              item.slug === slug ? { ...item, qty: Math.max(1, Math.round(quantity)) } : item
            )
            .filter((item) => item.qty > 0)
        );
      },
      removeItem(slug) {
        setItems((currentItems) => currentItems.filter((item) => item.slug !== slug));
      },
      clearCart() {
        setItems([]);
      }
    };
  }, [isReady, items]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used inside CartProvider");
  }

  return context;
}
